import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
    validSegmentationModes,
} from '../utils';

export const eventsOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color used for the background by using a hexadecimal code. For example, #0000FF.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    eventActions: {
        default: [],
        description:
            'Specify the available actions to take for an event. For example, `[{ "eventType": "eventAction.buildEventType.click", "label": "Build Event Type" }]`. Event types should end in `.click` if utilizing event handlers. Note that both `eventtypeValues` and `fieldFilters` keys support wildcard values.',
        type: 'array',
        items: {
            anyOf: [
                {
                    type: 'object',
                    properties: {
                        eventType: {
                            description: 'The distinct type for the event emitted upon event action click.',
                            type: 'string',
                            pattern: '.click$',
                            default: '',
                        },
                        label: {
                            description: 'The label for the event action.',
                            type: 'string',
                            default: '',
                        },
                        eventtypeValues: {
                            description:
                                'An array of string values that all need to match against the value(s) of the `eventtype` field if it exists in the event in order to be shown.',
                            items: { type: 'string' },
                            type: 'array',
                            default: [],
                        },
                        fieldFilters: {
                            description:
                                'An array of string values for fields that need to exist in the event in order for the given event action in order to be shown.',
                            items: { type: 'string' },
                            type: 'array',
                            default: [],
                        },
                    },
                    required: ['eventType', 'label'],
                },
            ],
        },
    },
    fieldActions: {
        description:
            'Specify the available actions for this field. For example, `{ *: [{ "eventType": "fieldAction.sharedAction1.click", "label": "Shared Action 1", "eventtypeValues": ["*"], "fieldFilters": ["*"] }] }`. Event types should end in `.click` if utilizing event handlers. Actions under the `*` key are shared across all field dropdowns. Note that both `eventtypeValues` and `fieldFilters` keys, as well as the field names, support wildcard values.',
        type: 'object',
        patternProperties: {
            '.*': {
                description: 'Specify the available actions for this field.',
                type: 'array',
                items: {
                    anyOf: [
                        {
                            type: 'object',
                            properties: {
                                eventType: {
                                    description:
                                        'The distinct type for the event emitted upon event action click.',
                                    type: 'string',
                                    pattern: '.click$',
                                    default: '',
                                },
                                label: {
                                    description: 'The label for the event action.',
                                    type: 'string',
                                    default: '',
                                },
                                eventtypeValues: {
                                    description:
                                        'An array of string values that all need to match against the value(s) of the `eventtype` field if it exists in the event in order to be shown.',
                                    items: { type: 'string' },
                                    type: 'array',
                                    default: [],
                                },
                                fieldFilters: {
                                    description:
                                        'An array of string values for fields that need to exist in the event in order for the given event action in order to be shown.',
                                    items: { type: 'string' },
                                    type: 'array',
                                    default: [],
                                },
                            },
                            required: ['eventType', 'label'],
                        },
                    ],
                },
            },
        },
    },
    footerFields: {
        description:
            'Specify field(s) that should be displayed in the footer component. Specify one or more fields to display in the footer component. For example, specify ["source", "dest"] to display the source and destination fields.',
        type: 'array',
        items: {
            type: 'string',
        },
        default: [],
    },
    highlightValuesByField: {
        description:
            'Specify the field and value pairs for manual highlighting. For example, `{"source": "$srcToken$", "dest": "12.21.1.11", "tag": ["tag1", "tag2"]}`.',
        type: 'object',
    },
    highlightFromSource: {
        description:
            'Specify a segmentation mode to use for search based highlighting. Specifying "none" will turn search based highlighting off. For more about segmentation, search for "event segmentation" in the Getting Data In manual in the Splunk Docs.',
        type: 'string',
        pattern: getPattern(validSegmentationModes as unknown as string[]),
        default: 'none',
    },
    pageCount: {
        description: 'Specify the maximum number of rows to display per page.',
        type: 'number',
        default: 10,
    },
    showFieldSummary: {
        description:
            'Specify that a field summary popup appears when a field is selected. To show field summaries for a search, configure an additional datasource called `fieldsummary` using the original search followed by the `... | fieldsummary maxvals=10` SPL command.',
        type: 'boolean',
        default: true,
    },
});
